import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  QuestionDefinitionDto,
  QuestionBlockType,
  QuestionDraft,
  QuestionListParams,
  QuestionListResult,
  QuestionListItem,
  QuestionPurpose,
  QuestionBrowseFacets,
  PublishJobDto,
  PublishJobSummary,
  QuestionTranslationFields,
  QuestionTranslationPatch,
  QuestionTranslationProjection,
  TranslationReplacementAction,
  WorkflowState,
} from '@course-authoring/shared-types';
import { buildListSearchParams, fetchWithAuthRetry } from './apiUtils';
import { invalidateLessonLocaleCoverage } from './lessonLocaleCoverageQueries';
import { retryPublishJob, type PublishJobApiError } from './publishJobApi';

export { isPublishJobConflict, retryPublishJob } from './publishJobApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Question Bank API. Lists are scoped by `externalAccountId` (NA-403):
 * omitted/null resolves to the Emtrain Standard bank only; a value resolves
 * to that client account's own private bank only, unless `includeStandard`
 * (the Question Bank page) adds the Standard bank to it. The scope is part
 * of the params object spread into the query key below, keeping Standard
 * and per-account list caches separate for free.
 */
export const questionKeys = {
  all: ['questions'] as const,
  lists: () => [...questionKeys.all, 'list'] as const,
  list: (params: QuestionListParams) => [...questionKeys.lists(), 'page', params] as const,
  infinite: (params: Omit<QuestionListFilters, 'page'>) =>
    [...questionKeys.lists(), 'infinite', params] as const,
  facets: (scope: QuestionBrowseScope) => [...questionKeys.all, 'facets', scope] as const,
};

/** The bank a browse read covers: Standard alone, or an account's bank with or without Standard. */
export interface QuestionBrowseScope {
  externalAccountId: number | null;
  includeStandard: boolean;
}

/** List params plus the NA-385 taxonomy label filters, the purpose and concept filters (Question Bank picker and page), and a capability list. */
export type QuestionListFilters = QuestionListParams & {
  taxonomyLabelId?: number;
  /** Any of these labels within a framework, in every framework named (the page's taxonomy panel). */
  taxonomyLabelIds?: readonly number[];
  /** With `externalAccountId`, the Standard bank as well (the page); the management list never sends it. */
  includeStandard?: boolean;
  blockTypes?: readonly QuestionBlockType[];
  /** Only questions with this stored purpose. */
  purpose?: QuestionPurpose;
  /** Only questions mapped to this content-catalog concept. */
  conceptId?: number;
};

export interface QuestionApiIssue {
  path: string;
  message: string;
}

export interface QuestionApiError extends PublishJobApiError {
  issues?: QuestionApiIssue[];
}

/** Fallback message shown when a question create fails without a server message. */
export const QUESTION_SAVE_ERROR_MESSAGE = 'Failed to save the question';

/** Narrows an unknown value to the field-level validation issues the API returns. */
export function narrowQuestionApiIssues(value: unknown): QuestionApiIssue[] {
  return Array.isArray(value)
    ? value.filter((issue): issue is QuestionApiIssue =>
      typeof issue === 'object' && issue !== null
      && typeof (issue as QuestionApiIssue).path === 'string'
      && typeof (issue as QuestionApiIssue).message === 'string')
    : [];
}

async function toQuestionApiError(response: Response, fallbackMessage: string): Promise<QuestionApiError> {
  let message = fallbackMessage;
  let issues: QuestionApiIssue[] | undefined;
  let code: string | undefined;
  let publishJob: PublishJobDto | null | undefined;
  try {
    const payload: unknown = await response.json();
    if (typeof payload === 'object' && payload !== null) {
      const record = payload as Record<string, unknown>;
      if (typeof record.message === 'string') message = record.message;
      if (Array.isArray(record.issues)) issues = narrowQuestionApiIssues(record.issues);
      if (typeof record.code === 'string') code = record.code;
      if (record.publishJob === null || (typeof record.publishJob === 'object' && record.publishJob !== null)) {
        publishJob = record.publishJob as PublishJobDto | null;
      }
    }
  } catch {
    // Non-JSON error body — keep the fallback message.
  }
  return { status: response.status, message, issues, code, publishJob };
}

export async function fetchQuestionDetail(id: number, locale: string): Promise<QuestionListItem> {
  const params = new URLSearchParams({ locale });
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${id}?${params.toString()}`);
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to refresh the question');
  const payload = await response.json() as { question: QuestionListItem };
  return payload.question;
}

export async function fetchQuestionPublishJob(id: number): Promise<PublishJobDto | null> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${id}/publish-job`);
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to refresh preparation status');
  const payload = await response.json() as { publishJob: PublishJobDto | null };
  return payload.publishJob;
}

export function useRetryPublishJobMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryPublishJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: questionKeys.all }),
  });
}

export function buildQuestionSearchParams(params: QuestionListFilters): URLSearchParams {
  const searchParams = buildListSearchParams(params);
  if (params.blockType) searchParams.set('blockType', params.blockType);
  if (params.blockTypes) searchParams.set('blockTypes', params.blockTypes.join(','));
  if (params.blockType === 'single-choice-answer' && typeof params.gated === 'boolean') {
    searchParams.set('gated', String(params.gated));
  }
  if (params.includeArchived) searchParams.set('includeArchived', 'true');
  if (params.locale) searchParams.set('locale', params.locale);
  if (typeof params.externalAccountId === 'number') {
    searchParams.set('externalAccountId', String(params.externalAccountId));
  }
  if (params.taxonomyLabelId !== undefined) searchParams.set('taxonomyLabelId', String(params.taxonomyLabelId));
  if (params.taxonomyLabelIds && params.taxonomyLabelIds.length > 0) searchParams.set('taxonomyLabelIds', params.taxonomyLabelIds.join(','));
  if (params.includeStandard && typeof params.externalAccountId === 'number') searchParams.set('includeStandard', 'true');
  if (params.purpose) searchParams.set('purpose', params.purpose);
  if (params.conceptId !== undefined) searchParams.set('conceptId', String(params.conceptId));
  return searchParams;
}

export async function fetchQuestions(params: QuestionListFilters): Promise<QuestionListResult> {
  const searchParams = buildQuestionSearchParams(params);
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions?${searchParams.toString()}`);
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to fetch questions');
  return await response.json() as QuestionListResult;
}

export async function fetchQuestionBrowseFacets(scope: QuestionBrowseScope): Promise<QuestionBrowseFacets> {
  const searchParams = new URLSearchParams();
  if (scope.externalAccountId !== null) {
    searchParams.set('externalAccountId', String(scope.externalAccountId));
    if (scope.includeStandard) searchParams.set('includeStandard', 'true');
  }
  const query = searchParams.toString();
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/facets${query ? `?${query}` : ''}`);
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to fetch the question bank filters');
  return await response.json() as QuestionBrowseFacets;
}

export function useQuestionPage(params: QuestionListParams) {
  return useQuery({
    queryKey: questionKeys.list(params),
    queryFn: () => fetchQuestions(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useInfiniteQuestions(
  params: Omit<QuestionListFilters, 'page'>,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: questionKeys.infinite(params),
    queryFn: ({ pageParam }) => fetchQuestions({
      ...params,
      page: pageParam,
      pageSize: params.pageSize ?? 25,
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((total, current) => total + current.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export async function createQuestion(
  draft: QuestionDraft,
): Promise<QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ draft }),
  });
  if (!response.ok) throw await toQuestionApiError(response, QUESTION_SAVE_ERROR_MESSAGE);
  const payload = await response.json() as { question: QuestionDefinitionDto; publishJob?: PublishJobSummary | null };
  return { ...payload.question, publishJob: payload.publishJob };
}

export function useCreateQuestionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draft }: { draft: QuestionDraft }) => createQuestion(draft),
    onSuccess: () => {
      // A new Standard question can surface in every scope's list; a new
      // account-scoped question surfaces only in that account's own list.
      // Broad invalidation is simplest and safe either way.
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
    },
  });
}

export async function updateQuestion(
  questionDefinitionId: number,
  draft: QuestionDraft,
  replacementAction?: TranslationReplacementAction,
): Promise<QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${questionDefinitionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ draft, replacementAction }),
  });
  if (!response.ok) throw await toQuestionApiError(response, QUESTION_SAVE_ERROR_MESSAGE);
  const payload = await response.json() as { question: QuestionDefinitionDto; publishJob?: PublishJobSummary | null };
  return { ...payload.question, publishJob: payload.publishJob };
}

export function useUpdateQuestionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionDefinitionId, draft, replacementAction }: {
      questionDefinitionId: number;
      draft: QuestionDraft;
      replacementAction?: TranslationReplacementAction;
    }) => updateQuestion(questionDefinitionId, draft, replacementAction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
      // NA-384: a base-locale edit (e.g. Title) must reach any lesson that
      // resolves this question — mirrors usePatchQuestionTranslationMutation
      // below, which already does this for translation edits. Without it, a
      // lesson already loaded in another tab/route keeps showing the bank
      // value from whenever it was last fetched until a hard reload.
      invalidateLessonLocaleCoverage(queryClient);
    },
  });
}

export async function patchQuestionTranslation(
  questionDefinitionId: number,
  locale: string,
  fields: QuestionTranslationFields,
): Promise<QuestionTranslationProjection & { publishJob?: PublishJobSummary | null }> {
  const search = new URLSearchParams({ locale });
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/questions/${questionDefinitionId}/translations?${search.toString()}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields } satisfies QuestionTranslationPatch),
    },
  );
  if (!response.ok) throw await toQuestionApiError(response, QUESTION_SAVE_ERROR_MESSAGE);
  const payload = await response.json() as { translation: QuestionTranslationProjection; publishJob?: PublishJobSummary | null };
  return { ...payload.translation, publishJob: payload.publishJob };
}

export function usePatchQuestionTranslationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionDefinitionId, locale, fields }: {
      questionDefinitionId: number;
      locale: string;
      fields: QuestionTranslationFields;
    }) => patchQuestionTranslation(questionDefinitionId, locale, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
      invalidateLessonLocaleCoverage(queryClient);
    },
  });
}

export async function archiveQuestion(questionDefinitionId: number): Promise<QuestionDefinitionDto> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${questionDefinitionId}/archive`, {
    method: 'POST',
  });
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to archive the question');
  const payload = await response.json() as { question: QuestionDefinitionDto };
  return payload.question;
}

export function useArchiveQuestionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionDefinitionId: number) => archiveQuestion(questionDefinitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
    },
  });
}

export async function unarchiveQuestion(questionDefinitionId: number): Promise<QuestionDefinitionDto> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${questionDefinitionId}/unarchive`, {
    method: 'POST',
  });
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to unarchive the question');
  const payload = await response.json() as { question: QuestionDefinitionDto };
  return payload.question;
}

export function useUnarchiveQuestionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionDefinitionId: number) => unarchiveQuestion(questionDefinitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
    },
  });
}

export async function deleteQuestion(questionDefinitionId: number): Promise<void> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${questionDefinitionId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to delete the question');
}

export function useDeleteQuestionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionDefinitionId: number) => deleteQuestion(questionDefinitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionKeys.all });
    },
  });
}

export interface QuestionUsageLesson {
  /** Obfuscated id — used for routing/links. */
  lessonId: string;
  /** Raw auto-increment id — used for the "L{n}" display id. */
  lessonNumericId: number;
  title: string;
  workflowState: WorkflowState;
  /** Owning scope of this usage entry: null = base (Emtrain) content, else the account id (NA-390). */
  externalAccountId: number | null;
  /** Resolved display name for account-scoped usages; null for base usages. */
  accountName: string | null;
}

export interface QuestionUsage {
  count: number;
  lessons: QuestionUsageLesson[];
}

export const EMPTY_QUESTION_USAGE: QuestionUsage = { count: 0, lessons: [] };

export async function fetchQuestionUsage(questionDefinitionId: number): Promise<QuestionUsage> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/${questionDefinitionId}/usage`);
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to fetch question usage');
  return await response.json() as QuestionUsage;
}

export function useQuestionUsageQuery(questionDefinitionId: number | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...questionKeys.all, 'usage', questionDefinitionId] as const,
    queryFn: () => fetchQuestionUsage(questionDefinitionId as number),
    enabled: (options?.enabled ?? true) && typeof questionDefinitionId === 'number',
  });
}

export async function fetchBulkQuestionUsage(
  questionDefinitionIds: number[],
): Promise<Record<number, QuestionUsage>> {
  if (questionDefinitionIds.length === 0) return {};
  const params = new URLSearchParams({ ids: questionDefinitionIds.join(',') });
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questions/usage?${params.toString()}`);
  if (!response.ok) throw await toQuestionApiError(response, 'Failed to fetch question usage');
  const payload = await response.json() as { usage: Record<number, QuestionUsage> };
  return payload.usage;
}

export function useBulkQuestionUsageQuery(questionDefinitionIds: number[]) {
  const sortedIds = [...questionDefinitionIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: [...questionKeys.all, 'usage', 'bulk', sortedIds] as const,
    queryFn: () => fetchBulkQuestionUsage(sortedIds),
    enabled: sortedIds.length > 0,
    placeholderData: (previous) => previous,
  });
}
